import React, { useState } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Menu,
  MenuItem,
  Container,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  useMediaQuery,
  Grid,
  useTheme,
  Avatar,
  Divider
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import { Link, useNavigate } from "react-router-dom";
import NotificationCenter from "./NotificationCenter";
import SmartSearch from "./SmartSearch";
import AuthModal from "./AuthModal";
import UserProfile from "./Auth/UserProfile";
import { useAuth } from "../contexts/AuthContext";

const Header = () => {
  const [metiersAnchor, setMetiersAnchor] = useState(null);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [userProfileOpen, setUserProfileOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const { user, isAuthenticated, loading, logout } = useAuth();

  const categoriesMetiers = {
    "Bâtiment & Rénovation": [
      { name: "Plombiers", path: "/m/plombiers", icon: "🔧", color: "#3b82f6", desc: "Urgences 24h/24" },
      { name: "Électriciens", path: "/m/electriciens", icon: "⚡", color: "#f59e0b", desc: "Installations & dépannages" },
      { name: "Peintres", path: "/m/peintres", icon: "🎨", color: "#ef4444", desc: "Devis automatisés" },
      { name: "Maçons", path: "/m/macons", icon: "🏗️", color: "#64748b", desc: "Gros œuvre & finitions" },
      { name: "Menuisiers", path: "/m/menuisiers", icon: "🪚", color: "#8b5cf6", desc: "Sur-mesure & rénovation" },
      { name: "Carreleurs", path: "/m/carreleurs", icon: "🏠", color: "#06b6d4", desc: "Pose & rénovation" },
      { name: "Couvreurs", path: "/m/couvreurs", icon: "🏘️", color: "#dc2626", desc: "Toitures & étanchéité" },
      { name: "Ramoneurs", path: "/m/ramoneurs", icon: "🔥", color: "#ea580c", desc: "Ramonage & certificats" }
    ],
    "Services à la personne": [
      { name: "Coiffeurs", path: "/m/coiffeurs", icon: "💇", color: "#ec4899", desc: "Réservations en ligne" },
      { name: "Esthéticiennes", path: "/m/estheticiennes", icon: "💅", color: "#f97316", desc: "Planning beauté" },
      { name: "Masseurs", path: "/m/masseurs", icon: "🤲", color: "#10b981", desc: "Bien-être & détente" },
      { name: "Services ménage", path: "/m/menage", icon: "🧹", color: "#06b6d4", desc: "Nettoyage pro" }
    ],
    "Automobile & Moto": [
      { name: "Garagistes", path: "/m/garagistes", icon: "🚗", color: "#1f2937", desc: "Réparations & entretien" },
      { name: "Carrossiers", path: "/m/carrossiers", icon: "🔧", color: "#7c3aed", desc: "Réparation carrosserie" },
      { name: "Contrôle technique", path: "/m/controle-technique", icon: "🛡️", color: "#059669", desc: "RDV automatisés" }
    ],
    "Commerce & Artisanat": [
      { name: "Fleuristes", path: "/m/fleuristes", icon: "🌸", color: "#ec4899", desc: "Créations & livraisons" },
      { name: "Jardiniers", path: "/m/jardiniers", icon: "🌱", color: "#10b981", desc: "Entretien espaces verts" }
    ]
  };

  const handleMetiersClick = (event) => {
    setMetiersAnchor(event.currentTarget);
  };

  const handleMetiersClose = () => {
    setMetiersAnchor(null);
  };

  const handleMetierSelect = (path) => {
    navigate(path);
    handleMetiersClose();
    setMobileDrawerOpen(false);
  };

  const handleAuthClick = () => {
    if (isAuthenticated) {
      setUserProfileOpen(true);
    } else {
      setAuthModalOpen(true);
    }
  };

  const getUserInitials = () => {
    if (!user) return '?';
    return `${user.firstName?.charAt(0) || ''}${user.lastName?.charAt(0) || ''}`.toUpperCase();
  };

  const getUserDisplayName = () => {
    if (!user) return 'Utilisateur';
    return `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email;
  };

  const mobileMenu = (
    <Drawer
      anchor="right"
      open={mobileDrawerOpen}
      onClose={() => setMobileDrawerOpen(false)}
    >
      <Box sx={{ width: 280, p: 2 }}>
        <List>
          <ListItem button component={Link} to="/">
            <ListItemText primary="Accueil" />
          </ListItem>
          
          <ListItem>
            <ListItemText 
              primary="Solutions par métier"
              sx={{ color: "#64748b", fontSize: "0.875rem" }}
            />
          </ListItem>
          
          {Object.entries(categoriesMetiers).map(([categorie, metiers]) => (
            <Box key={categorie}>
              <ListItem sx={{ pl: 2 }}>
                <ListItemText 
                  primary={categorie}
                  sx={{ 
                    color: "#1e293b", 
                    fontWeight: "bold", 
                    fontSize: "0.8rem" 
                  }}
                />
              </ListItem>
              {metiers.map((metier) => (
                <ListItem 
                  key={metier.path}
                  button 
                  onClick={() => handleMetierSelect(metier.path)}
                  sx={{ pl: 6 }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography sx={{ fontSize: "1rem" }}>
                      {metier.icon}
                    </Typography>
                    <ListItemText 
                      primary={metier.name}
                      secondary={metier.desc}
                      primaryTypographyProps={{ fontSize: "0.9rem" }}
                      secondaryTypographyProps={{ fontSize: "0.8rem" }}
                    />
                  </Box>
                </ListItem>
              ))}
            </Box>
          ))}
          
          <ListItem button onClick={() => navigate('/tarifs')}>
            <ListItemText primary="Tarifs" />
          </ListItem>
          
          <ListItem button onClick={() => navigate('/analytics')}>
            <ListItemText 
              primary="📊 Analytics" 
              primaryTypographyProps={{ 
                color: 'primary.main', 
                fontWeight: 'medium' 
              }}
            />
          </ListItem>
          
          {isAuthenticated && (
            <>
              <Divider sx={{ my: 1 }} />
              <ListItem>
                <ListItemText 
                  primary="Automatisations"
                  sx={{ color: "#64748b", fontSize: "0.875rem" }}
                />
              </ListItem>
              <ListItem button onClick={() => navigate('/relances')}>
                <ListItemText 
                  primary="🔔 Relances clients" 
                  secondary="Automatisation emails/SMS"
                  primaryTypographyProps={{ fontSize: "0.9rem" }}
                  secondaryTypographyProps={{ fontSize: "0.8rem" }}
                />
              </ListItem>
              <ListItem button onClick={() => navigate('/base-prix')}>
                <ListItemText 
                  primary="💰 Base prix métier" 
                  secondary="Catalogue prestations"
                  primaryTypographyProps={{ fontSize: "0.9rem" }}
                  secondaryTypographyProps={{ fontSize: "0.8rem" }}
                />
              </ListItem>
              <Divider sx={{ my: 1 }} />
            </>
          )}
          
          <ListItem button component={Link} to="/support" onClick={() => setMobileDrawerOpen(false)}>
            <ListItemText primary="Support" />
          </ListItem>
        </List>
        
        <Box sx={{ p: 2, mt: 2 }}>
          {isAuthenticated ? (
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body2" sx={{ mb: 2, color: '#64748b' }}>
                Connecté en tant que
              </Typography>
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold' }}>
                {getUserDisplayName()}
              </Typography>
              <Button
                fullWidth
                onClick={() => setUserProfileOpen(true)}
                sx={{ mb: 1 }}
              >
                👤 Mon profil
              </Button>
              <Button
                component={Link}
                to="/mes-sites-vitrines"
                fullWidth
                sx={{ mb: 1 }}
              >
                🌐 Mes sites vitrines
              </Button>
              <Button
                component={Link}
                to="/dashboard"
                variant="contained"
                fullWidth
                sx={{
                  bgcolor: "#3b82f6",
                  '&:hover': { bgcolor: "#2563eb" }
                }}
              >
                📊 Dashboard
              </Button>
            </Box>
          ) : (
            <>
              <Button
                variant="outlined"
                fullWidth
                sx={{ mb: 2 }}
                onClick={() => setAuthModalOpen(true)}
              >
                Se connecter
              </Button>
              <Button
                variant="contained"
                fullWidth
                sx={{
                  bgcolor: "#3b82f6",
                  '&:hover': { bgcolor: "#2563eb" }
                }}
                onClick={() => setAuthModalOpen(true)}
              >
                Essai gratuit
              </Button>
            </>
          )}
        </Box>
      </Box>
    </Drawer>
  );

  return (
    <>
      <AppBar 
        position="sticky" 
        elevation={0}
        sx={{ 
          bgcolor: "#ffffff",
          borderBottom: "1px solid #e2e8f0",
          color: "#1e293b"
        }}
      >
        <Container maxWidth="xl">
          <Toolbar sx={{ justifyContent: "space-between", py: 1 }}>
            {/* Logo */}
            <Box 
              component={Link} 
              to="/" 
              sx={{ 
                display: "flex", 
                alignItems: "center", 
                textDecoration: "none",
                color: "inherit"
              }}
            >
              <Box
                component="img"
                src="/lys-logo.png"
                alt="LYS Enterprise Solutions"
                sx={{
                  height: { xs: 32, md: 40 },
                  width: { xs: 32, md: 40 },
                  mr: 1.5,
                  borderRadius: 1
                }}
              />
              <Typography
                variant="h5"
                fontWeight="bold"
                sx={{
                  background: "linear-gradient(45deg, #3b82f6 30%, #06b6d4 90%)",
                  backgroundClip: "text",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  mr: 1,
                  fontSize: { xs: '1.1rem', md: '1.5rem' }
                }}
              >
                LYS Artisans
              </Typography>
            </Box>

            {/* Desktop Menu */}
            {!isMobile && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
                {/* Barre de recherche */}
                <Box sx={{ width: 280 }}>
                  <SmartSearch 
                    variant="header"
                    placeholder="Rechercher métier, ville..."
                    showTrending={false}
                  />
                </Box>

                {/* Menu Solutions par métier */}
                <Button
                  onClick={handleMetiersClick}
                  endIcon={<KeyboardArrowDownIcon />}
                  sx={{
                    color: "#1e293b",
                    fontWeight: "medium",
                    textTransform: "none",
                    whiteSpace: "nowrap",
                    '&:hover': {
                      bgcolor: "rgba(59, 130, 246, 0.04)"
                    }
                  }}
                >
                  Solutions
                </Button>

                <Button
                  component={Link}
                  to="/tarifs"
                  sx={{
                    color: "#1e293b",
                    fontWeight: "medium",
                    textTransform: "none"
                  }}
                >
                  Tarifs
                </Button>

                <Button
                  component={Link}
                  to="/analytics"
                  sx={{
                    color: 'primary.main',
                    fontWeight: "medium",
                    textTransform: "none",
                    '&:hover': {
                      bgcolor: (theme) => theme.palette.primary.main + '08'
                    }
                  }}
                >
                  📊 Analytics
                </Button>
                
                {isAuthenticated && (
                  <>
                    <Button
                      component={Link}
                      to="/relances"
                      sx={{
                        color: "#f59e0b",
                        fontWeight: "medium",
                        textTransform: "none",
                        '&:hover': {
                          bgcolor: "rgba(245, 158, 11, 0.08)"
                        }
                      }}
                    >
                      🔔 Relances
                    </Button>
                    
                    <Button
                      component={Link}
                      to="/base-prix"
                      sx={{
                        color: "#10b981",
                        fontWeight: "medium",
                        textTransform: "none",
                        '&:hover': {
                          bgcolor: "rgba(16, 185, 129, 0.08)"
                        }
                      }}
                    >
                      💰 Base prix
                    </Button>
                    
                    <Button
                      component={Link}
                      to="/mes-sites-vitrines"
                      sx={{
                        color: "#8b5cf6",
                        fontWeight: "medium",
                        textTransform: "none",
                        '&:hover': {
                          bgcolor: "rgba(139, 92, 246, 0.08)"
                        }
                      }}
                    >
                      🌐 Sites vitrines
                    </Button>
                  </>
                )}

                <Button
                  component={Link}
                  to="/support"
                  sx={{
                    color: "#1e293b",
                    fontWeight: "medium",
                    textTransform: "none"
                  }}
                >
                  Support
                </Button>
              </Box>
            )}

            {/* CTA Buttons */}
            {!isMobile && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                {/* NotificationCenter supprimé pour une expérience sans notifications */}
                {isAuthenticated ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Button
                      component={Link}
                      to="/dashboard"
                      variant="outlined"
                      sx={{
                        borderColor: "#3b82f6",
                        color: "#3b82f6",
                        textTransform: "none",
                        fontWeight: "medium"
                      }}
                    >
                      📊 Dashboard
                    </Button>
                    
                    <Button
                      onClick={handleAuthClick}
                      sx={{
                        minWidth: 'auto',
                        p: 1,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                      }}
                    >
                      {user?.avatar ? (
                        <Avatar 
                          src={user.avatar} 
                          sx={{ width: 32, height: 32 }}
                        />
                      ) : (
                        <Avatar 
                          sx={{ 
                            width: 32, 
                            height: 32, 
                            bgcolor: '#3b82f6',
                            fontSize: '14px'
                          }}
                        >
                          {getUserInitials()}
                        </Avatar>
                      )}
                      {!loading && (
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            color: '#1e293b',
                            fontWeight: 'medium',
                            ml: 1
                          }}
                        >
                          {user?.firstName || 'Profil'}
                        </Typography>
                      )}
                    </Button>
                  </Box>
                ) : (
                  <>
                    <Button
                      variant="outlined"
                      onClick={handleAuthClick}
                      disabled={loading}
                      sx={{
                        borderColor: "#3b82f6",
                        color: "#3b82f6",
                        textTransform: "none",
                        fontWeight: "medium"
                      }}
                    >
                      {loading ? '⏳' : 'Se connecter'}
                    </Button>
                    <Button
                      component={Link}
                      to="/tarifs"
                      variant="outlined"
                      sx={{
                        color: "#3b82f6",
                        borderColor: "#3b82f6",
                        textTransform: "none",
                        fontWeight: "medium",
                        mr: 1
                      }}
                    >
                      💰 Tarifs
                    </Button>
                    <Button
                      onClick={handleAuthClick}
                      disabled={loading}
                      variant="contained"
                      sx={{
                        bgcolor: "#3b82f6",
                        textTransform: "none",
                        fontWeight: "bold",
                        boxShadow: "0 4px 20px rgba(59, 130, 246, 0.25)",
                        '&:hover': {
                          bgcolor: "#2563eb",
                          boxShadow: "0 6px 25px rgba(59, 130, 246, 0.35)"
                        }
                      }}
                    >
                      {loading ? '⏳ Chargement...' : '🚀 S\'abonner'}
                    </Button>
                  </>
                )}
              </Box>
            )}

            {/* Mobile Menu Button */}
            {isMobile && (
              <IconButton
                onClick={() => setMobileDrawerOpen(true)}
                sx={{ color: "#1e293b" }}
              >
                <MenuIcon />
              </IconButton>
            )}
          </Toolbar>
        </Container>
      </AppBar>

      {/* MEGA MENU Métiers */}
      <Menu
        anchorEl={metiersAnchor}
        open={Boolean(metiersAnchor)}
        onClose={handleMetiersClose}
        PaperProps={{
          sx: {
            mt: 1,
            borderRadius: 3,
            boxShadow: "0 20px 60px rgba(0, 0, 0, 0.15)",
            border: "1px solid #e2e8f0",
            minWidth: 900, // Mega menu plus large
            maxWidth: 1000
          }
        }}
      >
        <Box sx={{ p: 4 }}>
          {/* Header Mega Menu */}
          <Box sx={{ mb: 4, textAlign: "center" }}>
            <Typography
              variant="h6"
              fontWeight="bold"
              sx={{ color: "#1e293b", mb: 1 }}
            >
              Solutions spécialisées par secteur d'activité
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "#64748b" }}
            >
              Chaque métier a ses spécificités. Découvrez votre solution personnalisée.
            </Typography>
          </Box>

          {/* Grid des catégories */}
          <Grid container spacing={4}>
            {Object.entries(categoriesMetiers).map(([categorie, metiers]) => (
              <Grid item xs={12} sm={6} md={3} key={categorie}>
                <Box>
                  {/* Titre catégorie */}
                  <Typography
                    variant="subtitle1"
                    fontWeight="bold"
                    sx={{
                      color: "#1e293b",
                      mb: 2,
                      pb: 1,
                      borderBottom: "2px solid #e2e8f0"
                    }}
                  >
                    {categorie}
                  </Typography>
                  
                  {/* Liste métiers */}
                  {metiers.map((metier) => (
                    <MenuItem
                      key={metier.path}
                      onClick={() => handleMetierSelect(metier.path)}
                      sx={{
                        borderRadius: 2,
                        mb: 1,
                        p: 2,
                        width: "100%",
                        '&:hover': {
                          bgcolor: `${metier.color}08`,
                          borderLeft: `3px solid ${metier.color}`,
                          transform: "translateX(4px)"
                        },
                        transition: "all 0.2s ease"
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, width: "100%" }}>
                        <Typography sx={{ fontSize: "1.2rem", mt: 0.5 }}>
                          {metier.icon}
                        </Typography>
                        <Box sx={{ flex: 1 }}>
                          <Typography 
                            variant="body2" 
                            fontWeight="medium" 
                            sx={{ color: "#1e293b", mb: 0.5 }}
                          >
                            {metier.name}
                          </Typography>
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              color: "#64748b", 
                              lineHeight: 1.3,
                              display: "block"
                            }}
                          >
                            {metier.desc}
                          </Typography>
                        </Box>
                      </Box>
                    </MenuItem>
                  ))}
                </Box>
              </Grid>
            ))}
          </Grid>

          {/* Footer Mega Menu */}
          <Box sx={{ 
            mt: 4, 
            pt: 3, 
            borderTop: "1px solid #e2e8f0",
            textAlign: "center"
          }}>
            <Typography variant="body2" sx={{ color: "#64748b", mb: 2 }}>
              Votre métier n'est pas listé ? Contactez-nous !
            </Typography>
            <Button
              variant="outlined"
              size="small"
              component={Link}
              to="/demo"
              sx={{
                borderColor: "#3b82f6",
                color: "#3b82f6",
                textTransform: "none"
              }}
            >
              Demander une démo personnalisée
            </Button>
          </Box>
        </Box>
      </Menu>

      {/* Mobile Drawer */}
      {mobileMenu}

      {/* Auth Modal */}
      <AuthModal 
        open={authModalOpen} 
        onClose={() => setAuthModalOpen(false)} 
      />

      {/* User Profile Modal */}
      {userProfileOpen && (
        <UserProfile onClose={() => setUserProfileOpen(false)} />
      )}
    </>
  );
};

export default Header;